import { useInfiniteQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { api } from '~/api';
import { useStoreState } from '~/store';
import type {
  ApiListing,
  ApiSolution,
  ActiveListingsResponse,
} from '../types/api';
import type { HomePageListing } from '../types/listings';

export type { HomePageListing };

export interface ActiveListingsPage {
  listings: HomePageListing[];
  nextPage: number | undefined;
  totalPages: number;
}

const mapListingType = (type: string): 'Tender' | 'Offer' | 'Bid' => {
  const map: Record<string, 'Tender' | 'Offer' | 'Bid'> = {
    tender: 'Tender',
    offer: 'Offer',
    bid: 'Bid',
  };
  return map[type] || 'Tender';
};

const mapListing = (
  listing: ApiListing,
  solutions: Record<string, ApiSolution>,
  formatMessage: ReturnType<typeof useIntl>['formatMessage']
): HomePageListing => {
  const solution = solutions[listing.solution_id];
  const isLive = listing.started && !listing.finished;

  let timingPrefix: 'Ends in' | 'Starts in' | 'Expires in' = 'Ends in';
  let timingDate = listing.finish;

  if (listing.listing_type === 'order') {
    timingPrefix = 'Expires in';
    timingDate = listing.expiry || listing.finish;
  } else if (!listing.started) {
    timingPrefix = 'Starts in';
    timingDate = listing.start;
  } else if (listing.type === 'offer') {
    timingPrefix = 'Expires in';
  }

  const counts: string[] = [];
  if (listing.offer_count > 0)
    counts.push(
      formatMessage(
        {
          id: 'active-listings-count-offer',
          defaultMessage: '{count, plural, one {# offer} other {# offers}}',
        },
        { count: listing.offer_count }
      )
    );
  if (listing.bid_count > 0)
    counts.push(
      formatMessage(
        {
          id: 'active-listings-count-bid',
          defaultMessage: '{count, plural, one {# bid} other {# bids}}',
        },
        { count: listing.bid_count }
      )
    );

  const displayType =
    listing.listing_type === 'order' && listing.type
      ? listing.type
      : listing.listing_type;

  const href =
    listing.listing_type === 'order'
      ? listing.status === 'Pending' || listing.status === 'Scheduled'
        ? `/orders/${listing.id}`
        : `/?order=${listing.pid}`
      : `/tenders/${listing.id}`;

  const badges: Array<{
    label: string;
    variant: 'counter' | 'pending' | 'scheduled';
    scheduledtime?: string;
  }> = [];
  if (listing.action_flags?.counter_owned) {
    badges.push({ label: 'counter', variant: 'counter' });
  }
  if (listing.listing_type === 'order' && listing.status === 'Pending') {
    badges.push({ label: 'pending', variant: 'pending' });
  } else if (
    listing.listing_type === 'order' &&
    listing.status === 'Scheduled'
  ) {
    badges.push({
      label: 'scheduled',
      variant: 'scheduled',
      scheduledtime: listing.scheduledtime,
    });
  }

  return {
    id: listing.id,
    href,
    solutionId: listing.solution_id,
    company: {
      name: solution?.name || listing.solution.name,
      icon: solution?.icon,
    },
    type: mapListingType(displayType),
    isOwner: listing.listing_type === 'order' && listing.is_owner,
    title:
      listing.listing_type === 'order'
        ? listing.product?.name || listing.name
        : listing.name,
    subtitle: counts.length > 0 ? counts.join(', ') : undefined,
    isLive,
    badges: badges.length > 0 ? badges : undefined,
    icons: (() => {
      const flagsToShow = [
        'qa_action_required',
        'counter_action_required',
      ] as const;
      const activeFlags = flagsToShow.filter(
        (flag) => listing.action_flags?.[flag] === true
      );
      return listing.isprivate || activeFlags.length > 0
        ? {
            isLocked: listing.isprivate,
            isFlagged: activeFlags.length > 0,
            activeFlags: activeFlags.length > 0 ? activeFlags : undefined,
          }
        : undefined;
    })(),
    timing: {
      prefix: timingPrefix,
      targetDate: timingDate,
      isEnded: listing.finished,
    },
    status: listing.listing_type === 'order' ? listing.status : undefined,
    attributes: listing.attributes,
  };
};

export const useActiveListings = () => {
  const user = useStoreState((state) => state.auth.user);
  const { formatMessage } = useIntl();

  return useInfiniteQuery({
    queryKey: ['activeListings', user?.id],
    queryFn: async ({ pageParam }): Promise<ActiveListingsPage> => {
      const data: ActiveListingsResponse = await api.getData2(
        `/active-listings?page=${pageParam}`
      );
      if (!data?.listings) {
        return { listings: [], nextPage: undefined, totalPages: 1 };
      }
      return {
        listings: data.listings.map((listing) =>
          mapListing(listing, data.solutions, formatMessage)
        ),
        nextPage: data._links?.next ? pageParam + 1 : undefined,
        totalPages: data.pages ?? 1,
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 30 * 1000,
    enabled: !!user,
  });
};
